<template>
  <!-- eslint-disable max-len -->
  <BaseFetchData :action="fetchData">
    <!-- props
      fetchData, status.loading, status.resolved, status.rejected, rejectReason
    -->
    <template #default="fetchDataProps">
      <div v-if="fetchDataProps.status.resolved" class="flex flex-col h-full">
        <div class="flex-none bg-white py-2">
          <div class="mx-auto px-4 flex items-center gap-3">
            <div class="flex-1">
              <input
                type="text"
                class="px-4 py-2 text-sm h-10 w-full focus:outline-none bg-gray-100 rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                v-model="nameInput"
              />
            </div>
            <div class="flex-none">
              <div class="flex space-x-1 rounded-lg bg-gray-100 p-0.5">
                <button
                  class="flex items-center rounded-md py-2 pl-2 pr-2 text-sm font-semibold lg:pr-3"
                  :class="[mode === 'edit' ? 'bg-white shadow' : '']"
                  role="tab"
                  type="button"
                  tabindex="-1"
                  @click="mode = 'edit'"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    :class="[mode === 'edit' ? 'text-blue-600' : 'text-gray-600']"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    /></svg
                  ><span class="sr-only lg:not-sr-only lg:ml-2 text-slate-600">Edit</span>
                </button>
                <button
                  class="flex items-center rounded-md py-2 pl-2 pr-2 text-sm font-semibold lg:pr-3"
                  :class="[mode === 'preview' ? 'bg-white shadow' : '']"
                  role="tab"
                  type="button"
                  tabindex="0"
                  @click="mode = 'preview'"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    :class="[mode === 'preview' ? 'text-blue-600' : 'text-gray-600']"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    /></svg
                  ><span class="sr-only lg:not-sr-only lg:ml-2 text-slate-900">Preview</span>
                </button>
              </div>
            </div>
            <div class="border-l border-gray-200 h-5" />
            <div class="flex-none flex space-x-3">
              <TestAction :email-template="emailTemplate" />
              <button
                type="button"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white disabled:text-gray-600 bg-green-600 disabled:bg-gray-200 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                :disabled="updating || !wip"
                @click="updateEmailTemplate"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 -ml-0.5 mr-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" /></svg
                ><span class="sr-only lg:not-sr-only">Update</span>
              </button>
            </div>
          </div>
        </div>
        <div class="flex-none px-4 pb-2 bg-white border-b space-y-2">
          <input
            type="text"
            class="px-4 py-2 text-sm h-10 w-full focus:outline-none bg-gray-100 rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            v-model="subjectInput"
            placeholder="Θέμα email"
          />
          <textarea
            v-model="contentInput"
            rows="1"
            class="px-4 py-2 text-sm w-full focus:outline-none bg-gray-100 rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            placeholder="κείμενο email"
          />
        </div>
        <div class="flex-1">
          <GrapesJsEditor
            v-show="mode === 'edit'"
            :project-data="emailTemplate.builder_data || defaultBuilderData"
            :on-load-assets="fetchAssets"
            :on-store-assets="storeAssets"
            :on-remove-asset="deleteAsset"
            v-model="contentEditorInput"
          />

          <iframe
            v-show="mode === 'preview'"
            src="about:blank"
            :srcdoc="emailPreview"
            class="w-full h-full"
          />
        </div>
      </div>
    </template>
  </BaseFetchData>
</template>

<script>
import { emailTemplateAssetsClient, emailTemplatesClient } from '@/api/clients';
import GrapesJsEditor from '@/components/GrapesJsEmailEditor/GrapesJsEmailEditor.vue';
import TestAction from './components/TestAction.vue';

export default {
  components: {
    TestAction,
    GrapesJsEditor,
  },
  props: {
    emailTemplateId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      emailTemplate: null,
      assets: [],
      defaultBuilderData: {
        pages: [
          {
            component: `
              <mjml>
                <mj-body padding="16px 0">
                  <mj-section>
                    <mj-column>
                      <mj-text>Insert text here</mj-text>
                    </mj-column>
                  </mj-section>
                </mj-body>
              </mjml>
            `,
          },
        ],
      },
      wip: null,
      mode: 'edit',
      updating: false,
    };
  },
  computed: {
    nameInput: {
      get() {
        return this.wip?.name || this.emailTemplate.name;
      },
      set(value) {
        this.wip = {
          ...(this.wip || {}),
          name: value,
        };
      },
    },
    subjectInput: {
      get() {
        return this.wip?.subject || this.emailTemplate.subject;
      },
      set(value) {
        this.wip = {
          ...(this.wip || {}),
          subject: value,
        };
      },
    },
    contentInput: {
      get() {
        return this.wip?.content || this.emailTemplate.content;
      },
      set(value) {
        this.wip = {
          ...(this.wip || {}),
          content: value,
        };
      },
    },
    contentEditorInput: {
      get() {
        return this.wip;
      },
      set({ data, mjml }) {
        this.wip = {
          ...(this.wip || {}),
          content_html: mjml.html,
          builder_data: { ...data, assets: [] },
        };
      },
    },
    emailPreview() {
      if (typeof this.wip?.content_html === 'string') {
        return this.wip.content_html;
      }

      if (typeof this.emailTemplate?.content_html === 'string') {
        return this.emailTemplate.content_html;
      }

      return '';
    },
  },
  methods: {
    async fetchData() {
      try {
        await Promise.all([this.fetchEmailTemplate()]);
      } catch (error) {
        //
      }
    },
    async fetchEmailTemplate() {
      try {
        const response = await emailTemplatesClient.read(this.emailTemplateId);

        this.emailTemplate = response.data;
      } catch (error) {
        //
      }
    },
    fetchAssets() {
      return emailTemplateAssetsClient.list(this.emailTemplateId);
    },
    storeAssets(data) {
      return emailTemplateAssetsClient.store(this.emailTemplateId, data).then(() => {
        this.fetchAssets();
      });
    },
    deleteAsset(mediaId) {
      return emailTemplateAssetsClient.destroy(this.emailTemplateId, mediaId).then(() => {
        this.fetchAssets();
      });
    },
    async updateEmailTemplate() {
      if (!this.wip) {
        return;
      }

      this.updating = true;

      try {
        const response = await emailTemplatesClient.update(this.emailTemplateId, this.wip);
        this.emailTemplate = response.data;
        this.wip = null;
        this.$toastr.success('Template Updated!');
      } catch (error) {
        this.$toastr.error(error.message || 'Update failed!');
      } finally {
        this.updating = false;
      }
    },
  },
};
</script>
